import Checkbox from "@app/components/checkbox";
import ConfirmModal from "@app/components/confirm-modal";
import ContractStatusModal from "@app/components/contract-status-modal";
import ChevronDownArrow from "@app/components/select/chevron-down";
import { CustomSelect } from "@app/components/select/custom-select";
import Table from "@app/components/table";
import {
  ColumnConfig,
  OptionsOnClick,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  BookOption,
  BookOptionName,
  BookOptions,
  BookType,
  BookTypeOptions,
  getContractStatusLabel,
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  RoutePath,
  SystemMessage,
} from "@app/constants";
import BookService from "@app/services/http/book.service";
import CategoryService from "@app/services/http/category.service";
import { openPortalDialog } from "@app/services/modal.service";
import { Book, DropdownCategory, Meta, RequestParamsBook } from "@app/types";
import { formatDateTime, formatMoneyVN } from "@app/utils/util";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RiInformationLine } from "react-icons/ri";

const MAX_AUTHOR_NAME_LENGTH = 25;

const getMediaTypeLabel = (mediaType?: number) => {
  switch (mediaType) {
    case 2:
      return "Ebook";
    case 3:
      return "Audio + Ebook";
    default:
      return "Audio";
  }
};

function OptionColumnTable({ data }: { data: Book }) {
  return (
    <div className="w-[85px]">
      <span
        className={clsx("px-4 py-2 rounded-lg text-xs", {
          "bg-[#E8FFF4] text-[#00A653]": data.bookOption === BookOption.FREE,
          "bg-[#FFF0E6] text-[#FE7E20]": data.bookOption === BookOption.VIP,
          "bg-[#CCE7FF] text-[#4558FF]":
            data.bookOption === BookOption.PURCHASE,
        })}
      >
        {BookOptionName[data.bookOption]}
      </span>
    </div>
  );
}

function AuthorColumnTable({ data }: { data: Book }) {
  const authorNames = data.authors?.map((author) => author.fullName).join(", ");
  const displayAuthorNames =
    authorNames && authorNames.length > MAX_AUTHOR_NAME_LENGTH
      ? `${authorNames.substring(0, MAX_AUTHOR_NAME_LENGTH)}...`
      : authorNames;

  return <div className="w-[250px]">{displayAuthorNames}</div>;
}

function PriceColumnTable({ data }: { data: Book }) {
  return (
    <div className="w-[160px]">
      <div>
        Audio: {typeof data.price === "number" ? `${formatMoneyVN(data.price)} đ` : "-"}
      </div>
      <div>
        Ebook: {typeof data.ebookPrice === "number" ? `${formatMoneyVN(data.ebookPrice)} đ` : "-"}
      </div>
    </div>
  );
}

function MediaTypeColumnTable({ data }: { data: Book }) {
  return (
    <div className="w-[140px]">
      <span className="rounded-lg bg-[#EEF2FF] px-3 py-2 text-xs font-semibold text-[#38499B]">
        {getMediaTypeLabel(data.mediaType)}
      </span>
    </div>
  );
}

function ReleaseColumnTable({
  data,
  onClick,
}: {
  data: Book;
  onClick: ({ options }: { options?: OptionsOnClick }) => void;
}) {
  const handleClick = () => {
    onClick && onClick({ options: { type: 1 } });
  };

  return (
    <div className="w-[70px] flex justify-center">
      <Checkbox checked={data.status === 1} onChange={handleClick} />
    </div>
  );
}

function ContractStatusColumnTable({
  data,
  onRefresh,
}: {
  data: Book;
  onRefresh: () => void;
}) {
  const handleClick = () => {
    const contractStatusModalObs = openPortalDialog(ContractStatusModal, {
      bookId: data.id,
      contractStatus: data.contractStatus || 1,
      contractRequestStatus: data.contractRequestStatus || 0,
      contractLink: data.contractLink,
      refreshCallback: onRefresh,
    });

    contractStatusModalObs.afterClosed().subscribe(() => {
      // Handle modal close if needed
    });
  };

  return (
    <div
      className="w-[200px] cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors border border-transparent hover:border-blue-200 text-blue-600 hover:text-blue-800"
      onClick={handleClick}
      title="Nhấn để xem chi tiết"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm">
          {getContractStatusLabel(data.contractStatus)}
        </span>
        <RiInformationLine className="text-[20px]" />
      </div>
    </div>
  );
}

function ReleaseTypeColumnTable({
  data,
  onClick,
}: {
  data: Book;
  onClick: ({ options }: { options?: OptionsOnClick }) => void;
}) {
  const handleClick = () => {
    onClick && onClick({ options: { type: 2 } });
  };

  return (
    <div className="w-[70px] flex justify-center">
      <Checkbox checked={data.releaseType === 3} onChange={handleClick} />
    </div>
  );
}

function BookNameColumnTable({
  data,
  onClick,
}: {
  data: Book;
  onClick: (book: Book) => void;
}) {
  return (
    <div
      className="w-[183px] truncate cursor-pointer hover:text-blue-600 hover:underline"
      onClick={() => onClick(data)}
    >
      {data.name}
    </div>
  );
}

function CreatedDateColumnTable({ data }: { data: Book }) {
  return <div>{formatDateTime(data.createdDate)}</div>;
}

function LastModifiedDateColumnTable({ data }: { data: Book }) {
  return <div>{formatDateTime(data.lastModifiedDate)}</div>;
}

function ActionColumnTable({
  data,
  onClick,
}: {
  data: Book;
  onClick: ({ options }: { options?: OptionsOnClick }) => void;
}) {
  const handleClick = () => {
    onClick && onClick({ options: { type: 3 } });
  };

  return (
    <div
      className={clsx(
        "bg-[#5570F1] hover:bg-[#3557ff] select-none text-white rounded-lg cursor-pointer px-4 py-2 w-full text-center"
      )}
      onClick={handleClick}
    >
      {data.isHasTranscription ? "Tạo lại srt" : "Tạo srt"}
    </div>
  );
}

function BookManagement() {
  const [books, setBooks] = useState<Book[]>([]);
  const [categories, setCategories] = useState<DropdownCategory[]>([]);
  const [requestParams, setRequestParams] = useState<RequestParamsBook>({
    ...DEFAULT_REQUEST_PARAMS,
    bookType: BookType.ALL.toString(),
  });
  const [meta, setMeta] = useState<Meta>();

  const navigate = useNavigate();

  const [update, forceUpdate] = useForceUpdate();
  const { abortController } = useCancellationRequest();

  const columnConfig: ColumnConfig[] = [
    {
      label: "ID",
      dataKey: "id",
      width: "183px",
      tdClassName: "truncate max-w-[183px]",
      hasOrder: true,
    },
    {
      label: "Tên sách",
      dataKey: "name",
      width: "183px",
      tdClassName: "max-w-[183px] leading-9",
      component: BookNameColumnTable,
      hasOrder: true,
    },
    {
      label: "Tác giả",
      dataKey: "author",
      width: "250px",
      tdClassName: "truncate max-w-[250px]",
      component: AuthorColumnTable,
      hasOrder: true,
    },
    {
      label: "Giới thiệu",
      dataKey: "description",
      width: "211px",
      tdClassName: "truncate max-w-[211px]",
      hasOrder: true,
    },
    {
      label: "Option",
      dataKey: "option",
      component: OptionColumnTable,
      width: "18%",
      hasOrder: true,
    },
    {
      label: "Giá",
      dataKey: "price",
      width: "180px",
      component: PriceColumnTable,
      hasOrder: true,
    },
    {
      label: "Media",
      dataKey: "mediaType",
      width: "160px",
      component: MediaTypeColumnTable,
      hasOrder: false,
    },
    {
      label: "Hành động",
      dataKey: "isHasTranscription",
      component: ActionColumnTable,
      width: "170px",
      theadClassName: "min-w-[170px]",
    },
    {
      label: "Phát hành",
      dataKey: "status",
      component: ReleaseColumnTable,
      width: "5%",
      hasOrder: true,
    },
    {
      label: "Trạng thái hợp đồng",
      dataKey: "contractStatus",
      component: (props: any) => (
        <ContractStatusColumnTable {...props} onRefresh={forceUpdate} />
      ),
      width: "220px",
      hasOrder: true,
    },
    {
      label: "Content + Audio",
      dataKey: "releaseType",
      component: ReleaseTypeColumnTable,
      width: "5%",
      hasOrder: true,
    },
    {
      label: "Người tạo",
      dataKey: "createdBy",
      width: "120px",
      tdClassName: "truncate min-w-[120px] max-w-[120px]",
      hasOrder: true,
    },
    {
      label: "Ngày tạo",
      dataKey: "createdDate",
      width: "180px",
      tdClassName: "truncate min-w-[180px] max-w-[180px]",
      component: CreatedDateColumnTable,
      hasOrder: true,
    },
    {
      label: "Người sửa cuối",
      dataKey: "lastModifiedBy",
      width: "140px",
      tdClassName: "truncate min-w-[120px] max-w-[140px]",
      hasOrder: true,
    },
    {
      label: "Ngày sửa cuối",
      dataKey: "lastModifiedDate",
      width: "180px",
      tdClassName: "truncate min-w-[180px] max-w-[180px]",
      component: LastModifiedDateColumnTable,
      hasOrder: true,
    },
  ];

  useEffect(() => {
    BookService.getList(requestParams, abortController.signal).then((res) => {
      setBooks(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [requestParams, update]);

  useEffect(() => {
    CategoryService.getDropdown({
      pageNum: 1,
      pageSize: 1000,
    }).then((res) => {
      setCategories(res.data.items);
    });
  }, []);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const onBookTypeChange = (e: string | number) => {
    const newRequestParams: RequestParamsBook = {
      ...requestParams,
      bookType: String(e),
    };

    setRequestParams(newRequestParams);
  };

  const onBookOptionChange = (e: string | number) => {
    const newRequestParams: RequestParamsBook = {
      ...requestParams,
      bookOption: Number(e),
    };

    setRequestParams(newRequestParams);
  };

  const onCategoryChange = (e: string | number) => {
    const newRequestParams: RequestParamsBook = {
      ...requestParams,
      categoryId: Number(e),
    };

    setRequestParams(newRequestParams);
  };

  const onCreateClick = () => {
    navigate(`/${RoutePath.BOOK_MANAGEMENT}/${RoutePath.CREATE_BOOK}`);
  };

  const onEditClick = (book: Book) => {
    navigate(
      `/${RoutePath.BOOK_MANAGEMENT}/${RoutePath.UPDATE_BOOK}/${book.id}`
    );
  };

  const handleDeleteClick = (bookId: number) => {
    if (bookId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_BOOK_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          BookService.delete(bookId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  const handleUpdateOrCreateTranscription = (bookId: number) => {
    if (bookId) {
      BookService.createTranscription(bookId).then(() => {
        addToast({ text: SystemMessage.CREATE_TRANSCRIPTION_SUCCESS });
        forceUpdate();
      });
    }
  };

  const handleDeletePermanentlyClick = (bookId: number) => {
    if (bookId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_PERMANENTLY_BOOK_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          BookService.deletePermanently(bookId).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  const handleRestoreClick = (bookId: number) => {
    if (bookId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.RESTORE_BOOK_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          BookService.restore(bookId).then(() => {
            addToast({ text: SystemMessage.RESTORE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  const handleReleaseTick = (book: Book) => {
    BookService.changeBookStatus(book.id).then(() => {
      forceUpdate();
    });
  };

  const handleReleaseTypeTick = (book: Book) => {
    BookService.changeBookReleaseType(book.id).then(() => {
      forceUpdate();
    });
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách sách</div>
        <div>
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={onCreateClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>Tạo sách mới</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <div className="flex gap-2">
          <div className="flex-[4]">
            <TitleDescription
              title="Danh sách sách"
              onInputChange={onSearchChange}
            />
          </div>
          <div className="flex-1">
            <CustomSelect
              options={BookTypeOptions}
              defaultValue={BookType.ALL.toString()}
              className="px-1 rounded-lg pr-4 !border-[#CFD3D4]"
              optionContainerClassName="overscroll-contain mt-1 rounded-b-lg overflow-y-auto"
              optionClassName="whitespace-nowrap px-2 py-2 border-b border-solid w-auto"
              labelSelectedClassName="black-50 !mx-3"
              placeholder="Book type"
              arrow={<ChevronDownArrow />}
              hasBgColorHover
              onChange={onBookTypeChange}
            />
          </div>
          <div className="flex-1">
            <CustomSelect
              options={BookOptions}
              className="px-1 rounded-lg pr-4 !border-[#CFD3D4]"
              optionContainerClassName="overscroll-contain mt-1 rounded-b-lg overflow-y-auto"
              optionClassName="whitespace-nowrap px-2 py-2 border-b border-solid w-auto"
              labelSelectedClassName="black-50 !mx-3"
              placeholder="Book option"
              arrow={<ChevronDownArrow />}
              hasBgColorHover
              onChange={onBookOptionChange}
            />
          </div>
          <div className="flex-1">
            <CustomSelect
              options={categories}
              className="px-1 rounded-lg pr-4 !border-[#CFD3D4]"
              optionContainerClassName="overscroll-contain mt-1 rounded-b-lg overflow-y-auto"
              optionClassName="whitespace-nowrap px-2 py-2 border-b border-solid w-auto"
              labelSelectedClassName="black-50 !mx-3"
              placeholder="Category"
              arrow={<ChevronDownArrow />}
              hasBgColorHover
              onChange={onCategoryChange}
            />
          </div>
        </div>
        <div className="mt-6">
          <Table
            columnConfig={columnConfig}
            dataTable={books}
            hasAction={requestParams.bookType !== BookType.TRASH.toString()}
            hasAction1={requestParams.bookType === BookType.TRASH.toString()}
            onEditClick={onEditClick}
            onDeleteClick={handleDeleteClick}
            onDeletePermanentlyClick={handleDeletePermanentlyClick}
            onRestoreClick={handleRestoreClick}
            meta={meta}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            customProps={{
              onClick: (props) => {
                const { data, options } = props;

                if (data) {
                  if (options?.type === 1) {
                    handleReleaseTick(data);
                  }

                  if (options?.type === 2) {
                    handleReleaseTypeTick(data);
                  }

                  if (options?.type === 3) {
                    handleUpdateOrCreateTranscription(data.id);
                  }

                  if (!options) {
                    onEditClick(data);
                  }
                }
              },
            }}
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default BookManagement;
